import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import PopoverMenuItem from "@entrilia/shared/components/PopoverMenuItem.tsx";
import type {
  ConditionConfiguration,
  MeasureDateRange,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { StatementDataColumnConfiguration } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import type { StatementLineState } from "../../../../store/types/fieldsState.types.ts";
import { createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import FieldOptionDeleteButton from "../../../viewer/common/fields/FieldOptionDeleteButton.tsx";
import FieldOptionsDivider from "../../../viewer/common/fields/FieldOptionsDivider.tsx";
import FieldPopover from "../../../viewer/common/fields/FieldPopover.tsx";
import PeriodSelect from "../common/PeriodSelect.tsx";
import { getDefaultColumnLabel, isDataColumn } from "../../../viewer/financialStatement/statementColumnLabels.ts";
import type { GetFieldCaption } from "../../../viewer/financialStatement/statementGridModel.ts";
import { getLivePeriodLineGuids } from "../utils/statementColumnPeriod.ts";
import {
  getColumnPeriodChanges,
  getColumnPeriodNote,
  getColumnPeriodOptions,
  getColumnPeriodScopeChanges,
  getPeriodRowOptions,
} from "../utils/statementColumns.ts";
import { getStatementLineLabel } from "../utils/statementRowsArea.ts";
import DataColumnLabelField from "./DataColumnLabelField.tsx";
import DataColumnPeriodScope from "./DataColumnPeriodScope.tsx";

interface Props {
  column: StatementDataColumnConfiguration;
  isOnlyDataColumn: boolean;
  labelConditions: ConditionConfiguration[];
  rowsPeriod: MeasureDateRange | undefined;
  lines: StatementLineState[];
  getFieldCaption: GetFieldCaption;
  removeBlockedMessage?: string;
  anchorEl: HTMLElement;
  readonly: boolean;
  onEditCalculation: () => void;
  onRemove: () => void;
  onClose: () => void;
}

const DataColumnOptionsPopover = ({
  column,
  isOnlyDataColumn,
  labelConditions,
  rowsPeriod,
  lines,
  getFieldCaption,
  removeBlockedMessage,
  anchorEl,
  readonly,
  onEditCalculation,
  onRemove,
  onClose,
}: Props) => {
  const dispatch = useAppDispatch();

  const updateColumn = (changes: Partial<StatementDataColumnConfiguration>) =>
    dispatch(createFinancialStatementAction("updateDataColumn", { guid: column.guid, changes }));

  const getLineLabel = (line: StatementLineState): string => getStatementLineLabel(line, getFieldCaption);

  const isFormula = !isDataColumn(column);

  return (
    <FieldPopover open anchorEl={anchorEl} onClose={onClose} sx={{ width: 400, p: 0 }}>
      <DataColumnLabelField
        customLabel={column.customLabel}
        defaultLabel={getDefaultColumnLabel(isFormula)}
        conditions={labelConditions}
        readonly={readonly}
        onChange={(customLabel) => updateColumn({ customLabel })}
      />
      <FieldOptionsDivider />
      {isFormula && !readonly && (
        <>
          <PopoverMenuItem sx={{ gap: 1 }} onClick={onEditCalculation}>
            <EditOutlinedIcon color="secondary" />
            <Typography>Edit Calculation...</Typography>
          </PopoverMenuItem>
          <FieldOptionsDivider />
        </>
      )}
      {!isFormula && (
        <>
          <Stack spacing={1} useFlexGap sx={{ px: 2, py: 1.5 }}>
            <PeriodSelect
              dateRange={column.dateRange}
              options={getColumnPeriodOptions(rowsPeriod)}
              note={getColumnPeriodNote(column.dateRange, column.periodScope)}
              readonly={readonly}
              onChange={(dateRange) => updateColumn(getColumnPeriodChanges(column, dateRange))}
            />
            {column.dateRange && (
              <DataColumnPeriodScope
                periodScope={column.periodScope}
                rowOptions={getPeriodRowOptions(lines, getLineLabel)}
                selectedGuids={getLivePeriodLineGuids(column, lines)}
                readonly={readonly}
                onScopeChange={(periodScope) => updateColumn(getColumnPeriodScopeChanges(column, periodScope))}
                onRowsChange={(periodLineGuids) => updateColumn({ periodLineGuids })}
              />
            )}
          </Stack>
          <FieldOptionsDivider />
        </>
      )}
      {!readonly && !isOnlyDataColumn && (
        <Tooltip title={removeBlockedMessage}>
          <Box>
            <FieldOptionDeleteButton disabled={removeBlockedMessage !== undefined} onRemove={onRemove}>
              Remove Column
            </FieldOptionDeleteButton>
          </Box>
        </Tooltip>
      )}
    </FieldPopover>
  );
};

export default DataColumnOptionsPopover;
